import { localStream } from '@stream'
import { androidConnector } from '@communication/remotes/AndroidConnector'

/**
 * Composable for requesting combined media permissions (camera + microphone)
 * Requests both in a single getUserMedia() call for better UX
 */
export default function useMediaPermissions() {

  const emptyResult = () => ({
    camera: { granted: false, track: null, error: null },
    microphone: { granted: false, track: null, error: null }
  })

  const queryPermissionState = async (name) => {
    try {
      const status = await navigator.permissions.query({ name })
      return status.state
    } catch (error) {
      // 'camera'/'microphone' are not queryable everywhere (Firefox < 131) —
      // treat as 'prompt' so we still attempt the request
      return 'prompt'
    }
  }

  const getCombinedStreams = async ({ requestCamera, requestMicrophone }) => {
    const result = emptyResult()

    // Build constraints for combined getUserMedia call
    const constraints = {}
    if (requestCamera) constraints.video = localStream.constraints.constraints.video || true
    if (requestMicrophone) constraints.audio = localStream.constraints.constraints.audio || true

    // Single getUserMedia call for both camera and microphone
    const stream = await navigator.mediaDevices.getUserMedia(constraints)

    // Process video track
    if (requestCamera) {
      const videoTracks = stream.getVideoTracks()
      if (videoTracks.length > 0) {
        const track = videoTracks[0]

        localStream.addExistingTrack('camera', track, { muted: {'video': false} })

        // Save device selection to localStorage
        const settings = track.getSettings()
        if (settings.deviceId) {
          localStream.constraints.update('camera', settings.deviceId, track.label)
        }

        result.camera.granted = true
        result.camera.track = track
      }
    }

    // Process audio track
    if (requestMicrophone) {
      const audioTracks = stream.getAudioTracks()
      if (audioTracks.length > 0) {
        const track = audioTracks[0]
        localStream.addExistingTrack('microphone', track, { muted: {'audio': false} })

        // Save device selection to localStorage
        const settings = track.getSettings()
        if (settings.deviceId) {
          localStream.constraints.update('microphone', settings.deviceId, track.label)
        }

        result.microphone.granted = true
        result.microphone.track = track
      }
    }

    return result
  }

  const handleCombinedFailure = async (error, { requestCamera, requestMicrophone }) => {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      // The combined prompt was denied or the permission is blocked. Requesting a
      // blocked device again auto-rejects without a prompt, so only re-request
      // devices whose permission can still be granted.
      const retryCamera = requestCamera && (await queryPermissionState('camera')) !== 'denied'
      const retryMicrophone = requestMicrophone && (await queryPermissionState('microphone')) !== 'denied'

      const result = (retryCamera || retryMicrophone)
        ? await requestIndividualStreams({ requestCamera: retryCamera, requestMicrophone: retryMicrophone })
        : emptyResult()

      if (requestCamera && !retryCamera) result.camera.error = error
      if (requestMicrophone && !retryMicrophone) result.microphone.error = error
      return result
    }

    // Other failures (NotFoundError, NotReadableError, ...) can be device-specific —
    // fall back to individual requests so the working device is still acquired
    return await requestIndividualStreams({ requestCamera, requestMicrophone })
  }

  /**
   * Request both camera and microphone in a single browser prompt
   */
  const requestMediaStreams = async ({
    requestCamera = true,
    requestMicrophone = true
  } = {}) => {
    try {
      return await getCombinedStreams({ requestCamera, requestMicrophone })
    } catch (error) {
      console.error('[requestMediaStreams] Error:', error)

      if (error.name === 'OverconstrainedError') {
        // A stored deviceId no longer matches any device: getUserMedia rejects
        // instantly without prompting. Drop stored selections, retry with defaults.
        if (requestCamera) localStream.constraints.delete('camera')
        if (requestMicrophone) localStream.constraints.delete('microphone')
        localStream.constraints.init()
        try {
          return await getCombinedStreams({ requestCamera, requestMicrophone })
        } catch (retryError) {
          console.error('[requestMediaStreams] Retry error:', retryError)
          return await handleCombinedFailure(retryError, { requestCamera, requestMicrophone })
        }
      }

      return await handleCombinedFailure(error, { requestCamera, requestMicrophone })
    }
  }

  /**
   * Fallback: Request camera and microphone individually when combined request fails
   */
  const requestIndividualStreams = async ({ requestCamera, requestMicrophone }) => {
    const result = {
      camera: { granted: false, track: null, error: null },
      microphone: { granted: false, track: null, error: null }
    }

    // Try camera individually
    if (requestCamera) {
      try {
          const track = await localStream.addTrack('camera', { muted: {'video': false} })

          const settings = track.getSettings()
          if (settings.deviceId) localStream.constraints.update('camera', settings.deviceId, track.label)

          result.camera.granted = true
          result.camera.track = track
      } catch (err) {
        result.camera.error = err
      }
    }

    // Try microphone individually
    if (requestMicrophone) {
      try {
          const track = await localStream.addTrack('microphone', { muted: {'audio': false} })
          const settings = track.getSettings()
          if (settings.deviceId) {
            localStream.constraints.update('microphone', settings.deviceId, track.label)
          }

          result.microphone.granted = true
          result.microphone.track = track
      } catch (err) {
        result.microphone.error = err
      }
    }

    return result
  }

  /**
   * Request media streams for Android devices
   */
  const requestAndroidMediaStreams = async ({ 
    requestCamera = true, 
    requestMicrophone = true 
  } = {}) => {
    const result = {
      camera: { granted: false, track: null, error: null },
      microphone: { granted: false, track: null, error: null }
    }

    try {
      if (requestCamera) {
        const track = await androidConnector.getTrack('video')
        localStream.video.addTrack(track, 'android')
        result.camera.granted = true
        result.camera.track = track
      }

      if (requestMicrophone) {
        const track = await androidConnector.getTrack('audio')
        localStream.audio.addTrack(track, 'android')
        result.microphone.granted = true
        result.microphone.track = track
      }
    } catch (error) {
      if (requestCamera) result.camera.error = error
      if (requestMicrophone) result.microphone.error = error
    }

    return result
  }

  return {
    requestMediaStreams,
    requestAndroidMediaStreams,
    requestIndividualStreams
  }
}
